import { get, post, put } from "aws-amplify/api";
import React, { useMemo, useState, useEffect } from "react";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import WithholdingList from "containers/property/WithholdingList";
import "containers/common/List.css";
import "containers/common/Table.css";
import { createElementIdFactory, createWithholdingProofingElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";


function WithholdingProofingList({
  isAuthenticated,
  filter,
  user,
  deleteRecord,
  userPermissions,
  withholdingReasons,
  classificationOptions,
  saveForm, }) {

  const [isLoading, setIsLoading] = useState(true);
  const [withholdings, setWithholdings] = useState([]);
  const [cityscopeOptions, setCityscopeOptions] = useState([]);
  const [selectedCityscope, setSelectedCityscope] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedWithholdingReason, setSelectedWithholdingReason] = useState(null);
  const [selectedClassification, setSelectedClassification] = useState(null);
  const [errorMessage,] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [showWarning,] = useState(false);
  const [propertykey, setPropertykey] = useState("");
  const [stateUser, setStateUser] = useState("");
  const [location, setLocation] = useState("");
  const [classification, setClassification] = useState("");
  const [withholdingreason, setWithholdingreason] = useState("");
  const [cityscope, setCityscope] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const createId = useMemo(() => createElementIdFactory("withholding-proofing"), []);
  const elementIds = useMemo(() => createWithholdingProofingElementIds(createId), [createId]);
  const filterControlStyle = {
    minHeight: "38px",
    width: "100%",
  };
  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: 38,
      height: 38,
      borderColor: "#ced4da",
      boxShadow: "none",
      width: "100%",
    }),
    valueContainer: (base) => ({
      ...base,
      height: 38,
      padding: "0 8px",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: 38,
    }),
  };

  function buildWithholdingQueryParams() {
    return {
      propertykey: propertykey || "",
      user: stateUser || "",
      location: location || "",
      classification: classification || "",
      withholdingreason: withholdingreason || "",
      cityscope: cityscope || "",
    };
  }


  // --- Data Fetching and Update Functions ---

  useEffect(() => {
    document.title = "HOLD Proofing";

    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        const localCityscopeOptions = await getCityscopes();
        if (localCityscopeOptions) {
          setCityscopeOptions(localCityscopeOptions);
        }
        const localWithholdings = await getWithholdings(buildWithholdingQueryParams());
        setWithholdings(localWithholdings);
      } catch (e) {
        alert(e);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);


  async function getCityscopes() {
    const response = await get({
      apiName: "colin",
      path: "/cityscopes/dropdown",
    }).response;
    try {
      const cs = await response.body.json();
      return cs;
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }


  async function getWithholdings(filter) {
    const options = {
      queryParams: filter,
    };
    const response = await get({
      apiName: "colin",
      path: "/withholdings",
      options,
    }).response;
    try {
      const ent = await response.body.json();
      return Array.isArray(ent)
        ? ent
        : ent?.items || ent?.data || ent?.results || ent?.withholdings || [];
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }


  async function bulkSaveRecords(records) {
    let lastResult = null;

    for (const record of records) {
      const response = await put({
        apiName: "colin",
        path: `/properties/${record["property_key"]}/withholdings/${record["property_withholding_id"]}`,
        options: {
          body: record,
        },
      }).response;
      try {
        const wp = await response.body.json();
        lastResult = wp;
      } catch (error) {
        alert(error + ". Please refresh the page (F5) to try again.");
      }
    }

    return lastResult;
  }


  async function saveRecord(record) {
    record.last_edited_by = user;
    //If the record is a new record perform a post
    if (record.hasOwnProperty("new_record")) {
      if (record.new_record === true) {
        record.last_edited_by = user;

        const response = await post({
          apiName: "colin",
          path: `/properties/${record["property_key"]}/withholdings/new`,
          options: {
            body: record,
          },
        }).response;

        try {
          const res = await response.body.json();
          return res;
        } catch (error) {
          alert(error + ". Please refresh the page (F5) to try again.");
        }
      }
    }

    const response = await put({
      apiName: "colin",
      path: `/properties/${record["property_key"]}/withholdings/${record["property_withholding_id"]}`,
      options: {
        body: record,
      },
    }).response;

    try {
      const res = await response.body.json();
      return res;
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }


  async function handleSubmit(event) {
    event.preventDefault();
    setHasSearched(true);
    setIsLoading(true);

    const localwithholdings = await getWithholdings(buildWithholdingQueryParams());

    setWithholdings(localwithholdings);
    setIsLoading(false);
  }

  async function refreshWithholdings() {
    setIsLoading(true);
    const localwithholdings = await getWithholdings(buildWithholdingQueryParams());
    setWithholdings(localwithholdings);
    setIsLoading(false);
  }


  function updateWithholdingTableState(rowIndex, columnId, value) {
    setWithholdings((currentWithholdings) => {
      if (!Array.isArray(currentWithholdings)) {
        return currentWithholdings;
      }

      return currentWithholdings.map((record, index) => {
        if (index !== rowIndex) {
          return record;
        }

        return {
          ...record,
          [columnId]: value,
        };
      });
    });
  }


  function validateRecord(record) {
    return [true];
  }


  async function handleCityscopeChange(selectedCityscope) {
    if (selectedCityscope) {
      setSelectedCityscope(selectedCityscope);
      setCityscope(selectedCityscope.map((r) => r.value).join(","));
    } else {
      setSelectedCityscope(selectedCityscope);
      setCityscope("");
    }
  }


  async function handleLocationChange(selectedLocation) {
    if (selectedLocation) {
      setSelectedLocation(selectedLocation);
      setLocation(selectedLocation.value);
    } else {
      setSelectedLocation(selectedLocation);
      setLocation("");
    }
  }


  async function handleWithholdingReasonChange(selectedWithholdingReason) {
    if (selectedWithholdingReason) {
      setSelectedWithholdingReason(selectedWithholdingReason);
      setWithholdingreason(selectedWithholdingReason.value);
    } else {
      setSelectedWithholdingReason(selectedWithholdingReason);
      setWithholdingreason("");
    }
  }


  async function handlePrimaryClassificationChange(selectedClassification) {
    if (selectedClassification) {
      setSelectedClassification(selectedClassification);
      setClassification(selectedClassification.value);
    } else {
      setSelectedClassification(selectedClassification);
      setClassification("");
    }
  }

  function handleRefreshList(event) {
    if (event) {
      event.preventDefault();
    }
    handleSubmit(event || { preventDefault: () => {} });
  }


  // --- Render Functions ---

  function renderTable() {
    return (
      <WithholdingList
        filter={filter}
        isAuthenticated={isAuthenticated}
        isLoading={isLoading}
        controlType="table"
        saveForm={saveForm}
        user={user}
        data={withholdings}
        updateTableState={updateWithholdingTableState}
        saveRecord={saveRecord}
        bulkSaveRecords={bulkSaveRecords}
        refreshRecords={refreshWithholdings}
        deleteRecord={deleteRecord}
        validateRelatedRecord={validateRecord}
        withholdingReasons={withholdingReasons}
        userPermissions={userPermissions}
        pageSize={PAGE_SIZES.holdChecks}
      />
    );
  }


  function renderComponent() {
    // const activeTable = tablesMap[controlType] || tablesMap.table;
    const showResults = hasSearched || (withholdings && withholdings.length > 0);
    return (
      <div className="withholdings">
        <Modal
          show={showAlert}
          onHide={(e) => {
            setShowAlert(!showAlert)
          }}
          centered
        >
          <Modal.Header closeButton>
            {" "}
            <Modal.Title>Uh Oh!</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {" "}
            <Alert variant="danger">{errorMessage}</Alert>{" "}
          </Modal.Body>
        </Modal>
        {showWarning && <Alert variant="danger">{errorMessage}</Alert>}
        <Form onSubmit={handleSubmit}>
          <Row className="g-2 align-items-end flex-wrap">
            <Col style={{ flex: "1 1 240px", minWidth: "240px" }}>
              <Form.Group controlId={elementIds.propertyKey}>
                <Form.Control
                  id={elementIds.propertyKey}
                  value={propertykey}
                  onChange={e => setPropertykey(e.target.value)}
                  type="input"
                  placeholder="Cityscope Reference starting"
                  style={filterControlStyle}
                />
              </Form.Group>
            </Col>

            <Col style={{ flex: "1 1 160px", minWidth: "160px" }}>
              <Form.Group controlId={elementIds.userName}>
                <Form.Control
                  id={elementIds.userName}
                  value={stateUser}
                  onChange={e => setStateUser(e.target.value)}
                  type="input"
                  placeholder="Username"
                  style={filterControlStyle}
                />
              </Form.Group>
            </Col>

            <Col style={{ flex: "1 1 160px", minWidth: "160px" }}>
              <Form.Group controlId={elementIds.location}>
                <Select
                  inputId={elementIds.location}
                  value={selectedLocation}
                  onChange={handleLocationChange}
                  options={[
                    { value: "onshore", label: "Onshore" },
                    { value: "offshore", label: "Offshore" },
                  ]}
                  placeholder="Location"
                  isClearable
                  styles={selectStyles}
                />
              </Form.Group>
            </Col>
            <Col style={{ flex: "1 1 160px", minWidth: "160px" }}>
              <Form.Group controlId={elementIds.cityscope}>
                <Select
                  inputId={elementIds.cityscope}
                  value={selectedCityscope}
                  onChange={handleCityscopeChange}
                  options={cityscopeOptions}
                  placeholder="Cityscope"
                  isClearable
                  isMulti
                  styles={selectStyles}
                />
              </Form.Group>
            </Col>
            <Col style={{ flex: "1 1 170px", minWidth: "170px" }}>
              <Form.Group controlId={elementIds.holdReason}>
                <Select
                  inputId={elementIds.holdReason}
                  value={selectedWithholdingReason}
                  onChange={handleWithholdingReasonChange}
                  options={withholdingReasons}
                  placeholder="HOLD Reason"
                  isClearable
                  styles={selectStyles}
                />
              </Form.Group>
            </Col>
            <Col style={{ flex: "1 1 180px", minWidth: "180px" }}>
              <Form.Group controlId={elementIds.primaryClassification}>
                <Select
                  inputId={elementIds.primaryClassification}
                  value={selectedClassification}
                  onChange={handlePrimaryClassificationChange}
                  options={classificationOptions}
                  placeholder="Primary Classification"
                  isClearable
                  styles={selectStyles}
                />
              </Form.Group>
            </Col>
            <Col style={{ flex: "0 0 120px", minWidth: "120px" }}>
              <Button id={elementIds.searchButton} type="submit" className="w-100 form-control-height">
                Search
              </Button>
            </Col>
            <Col style={{ flex: "0 0 140px", minWidth: "140px" }}>
              <Button id={elementIds.refreshButton} type="button" className="w-100 form-control-height" onClick={handleRefreshList}>
                Refresh List
              </Button>
            </Col>
          </Row>
        </Form>

        {showResults && renderTable()}
      </div>
    );
  }
  return (
    <div className="WithholdingList">
      {isAuthenticated ? renderComponent() : null}
    </div>
  );
}
export default WithholdingProofingList;
